import { useCallback } from 'react';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { elementQuantityUnitRecord } from '@shared/models/element-quantities.model';
import type { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';
import type { IElement } from '@shared/models/project.model';
import type { QuantityUnit } from '@shared/models/unit.model';

/**
 * Returns a stable callback that opens the {@link expressionPanel} for a specific quantity's
 * `count` expression, then applies the result via {@link ProjectChangeType.ElementQuantityCount}.
 *
 * Clearing the input (saving with an empty expression) sets `count` to `undefined`, which
 * lets the element's `fallbackCount` take effect again. Apply-layer validation errors are
 * surfaced by {@link useApplyProjectChanges} (`showErrorsInSnackbar: true`).
 *
 * @param element - Row element used to **read** `quantity` / `count` for the panel (under a
 * selected proposal, pass the same resolved node the list row displays so values match baseline
 * vs proposal overrides). {@link ProjectChangeType.ElementQuantityCount} still targets
 * {@link IElement.id}.
 * @param quantityName - Which quantity expression property to edit (e.g. `"area_top"`).
 * @param readonly - When `true` the callback is a no-op.
 */
export const useQuantityCountPanel = (
  element: IElement,
  quantityName: ElementQuantityExpressionName,
  readonly = false,
): (() => Promise<void>) => {
  const applyChanges = useApplyProjectChanges({ showErrorsInSnackbar: true });
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(async () => {
    if (readonly) return;

    const quantityRow = element.quantity?.[quantityName];
    if (!quantityRow) return;

    const unit = elementQuantityUnitRecord[quantityName] as QuantityUnit;

    try {
      const { result, type } = await expressionPanel.open({
        referenceId: `${element.id}-quantity-count`,
        elementId: element.id,
        expressionValue: quantityRow.count,
        fallbackExpressionValue: quantityRow.fallbackCount,
        unit,
      });

      if (type !== 'save') return;

      await applyChanges([
        {
          type: ProjectChangeType.ElementQuantityCount,
          elementId: element.id,
          quantityName,
          // undefined when expression was cleared → fallbackCount takes over
          count: result.expressionValue,
        },
      ]);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : String(e), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [applyChanges, element, enqueueSnackbar, quantityName, readonly]);
};
